import { useCallback, useEffect, useRef, useState } from "react";
import { Activity, AlertTriangle, Clock, Loader, RefreshCw } from "lucide-react";

// =====================================================================
// MonitoringPage — aktuelle Healthchecks der registrierten Satellite-Apps
//
// Die Seite verwendet ausschliesslich GET /api/monitoring/status. Dieser
// Endpoint liefert keine Railway-/Deploy-/Kosten-/externen Uptime-Daten.
// Coolify und UptimeRobot werden nur als Betriebsquellen eingeordnet; ihre
// Daten werden hier nicht behauptet oder abgefragt.
// =====================================================================

type StatusTone = "green" | "yellow" | "red" | "gray";
type SnapshotFreshness = "fresh" | "stale" | "unknown";
type SamplingCoverage = "no_samples" | "partial_24h" | "full_24h";

interface HealthService {
  appClaim: string;
  name: string;
  basePath: string;
  configured: boolean;
  tone: StatusTone;
  status: "healthy" | "unhealthy" | "unreachable" | "not_configured" | "unknown";
  lastKnownStatus?: string | null;
  // Optional, damit alte lokale Fixtures weiterhin lesbar sind. Ohne eine
  // bestätigte Freshness zeigt die UI bewusst keinen grünen Zustand.
  freshness?: SnapshotFreshness;
  dataAgeMs?: number | null;
  coverage?: SamplingCoverage;
  lastLatencyMs: number | null;
  avgLatencyMs: number | null;
  uptimePct: number | null;
  consecutiveFailures: number;
  lastCheckedAt: string | null;
  lastError: string | null;
  samples: number;
}

interface HealthSnapshot {
  generatedAt: string;
  intervalSeconds: number;
  windowHours: number;
  alertThreshold: number;
  services: HealthService[];
  alerts: Array<{
    appClaim: string;
    name: string;
    consecutiveFailures: number;
    lastError: string | null;
    since: string | null;
  }>;
}

interface SourceStatus {
  connected: boolean;
  status: "unknown";
  note: string;
}

interface MonitoringData {
  health: HealthSnapshot;
  sources?: {
    coolify?: SourceStatus;
    uptimeRobot?: SourceStatus;
  };
  fetchedAt: string;
}

const MONITORING_CONTRACT_HEADER = "X-Monitoring-Contract";
const MONITORING_CONTRACT_VERSION = "health-v2";
const INVALID_MONITORING_RESPONSE = "Ungültige Monitoring-Antwort. Bitte die Seite neu laden.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isTimestamp(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && Number.isFinite(Date.parse(value));
}

function isRenderableHealthService(value: unknown): value is HealthService {
  // Protects the map/key/property reads in ServiceCard. Further health fields
  // deliberately remain forward-compatible and are rendered defensively.
  return isRecord(value) && typeof value.appClaim === "string" && typeof value.name === "string";
}

function isRenderableHealthAlert(value: unknown): boolean {
  return isRecord(value) && typeof value.appClaim === "string" && typeof value.name === "string";
}

function isMonitoringData(value: unknown): value is MonitoringData {
  if (!isRecord(value) || !isRecord(value.health) || !isTimestamp(value.fetchedAt)) return false;
  const health = value.health;
  return isTimestamp(health.generatedAt)
    && Array.isArray(health.services)
    && health.services.every(isRenderableHealthService)
    && Array.isArray(health.alerts)
    && health.alerts.every(isRenderableHealthAlert);
}

function formatTimeAgo(isoString: string | null): string {
  if (!isoString) return "—";
  const d = new Date(isoString);
  const diffMs = Math.max(0, Date.now() - d.getTime());
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMs / 3_600_000);
  const diffD = Math.floor(diffMs / 86_400_000);
  const timeStr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const dateStr = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  if (diffMin < 1) return "gerade eben";
  if (diffMin < 60) return `vor ${diffMin} Min`;
  if (diffH < 24) return `vor ${diffH}h (${timeStr})`;
  if (diffD < 7) return `vor ${diffD}d (${dateStr})`;
  return `${dateStr} ${timeStr}`;
}

function formatDateTime(isoString: string | null): string {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAge(dataAgeMs: number | null | undefined): string {
  if (dataAgeMs == null) return "unbekannt";
  if (dataAgeMs < 60_000) return "unter 1 Min";
  if (dataAgeMs < 3_600_000) return `${Math.floor(dataAgeMs / 60_000)} Min`;
  return `${Math.floor(dataAgeMs / 3_600_000)} Std`;
}

function toneToColor(tone: StatusTone): string {
  switch (tone) {
    case "green": return "var(--ok)";
    case "yellow": return "var(--warn)";
    case "red": return "var(--bad)";
    default: return "var(--text-3)";
  }
}

function serviceFreshness(service: HealthService): SnapshotFreshness {
  // Fehlende Freshness in einer alten Fixture ist keine aktuelle Erfolgsmeldung.
  if (service.freshness !== "fresh") return service.freshness ?? "unknown";
  const checkedAt = service.lastCheckedAt ? Date.parse(service.lastCheckedAt) : NaN;
  if (!Number.isFinite(checkedAt)) return "unknown";
  // Auch ohne erfolgreiche Aktualisierung altert ein früher grüner Snapshot.
  return Date.now() - checkedAt > 180_000 ? "stale" : "fresh";
}

function displayedTone(service: HealthService): StatusTone {
  return serviceFreshness(service) === "fresh" ? service.tone : "gray";
}

function statusLabel(service: HealthService): string {
  const freshness = serviceFreshness(service);
  if (freshness === "stale") return "Veraltet";
  if (freshness === "unknown") return "Unbekannt";

  const map: Record<HealthService["status"], string> = {
    healthy: "Gesund",
    unhealthy: "Beeinträchtigt",
    unreachable: "Nicht erreichbar",
    not_configured: "Nicht konfiguriert",
    unknown: "Unbekannt",
  };
  return map[service.status];
}

function coverageLabel(service: HealthService): string {
  switch (service.coverage) {
    case "full_24h":
      return `Volles 24-h-Fenster (${service.samples} Stichproben)`;
    case "partial_24h":
      return `Stichprobe, noch kein volles 24-h-Fenster (${service.samples})`;
    default:
      return "Keine Stichproben";
  }
}

function pollingAllowed(): boolean {
  const visible = typeof document === "undefined" || document.visibilityState !== "hidden";
  const online = typeof navigator === "undefined" || navigator.onLine !== false;
  return visible && online;
}

function AmpelDot({ tone, size = 10 }: { tone: StatusTone; size?: number }) {
  return (
    <span
      aria-label={`Status ${tone}`}
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: toneToColor(tone),
        boxShadow: tone !== "gray" ? `0 0 6px ${toneToColor(tone)}` : undefined,
        flexShrink: 0,
      }}
    />
  );
}

function AmpelSummary({ services }: { services: HealthService[] }) {
  const fresh = services.filter((service) => serviceFreshness(service) === "fresh");
  const healthy = fresh.filter((service) => displayedTone(service) === "green").length;
  const unknown = services.length - fresh.length;
  const tone: StatusTone = fresh.length === 0
    ? "gray"
    : fresh.some((service) => displayedTone(service) === "red")
      ? "red"
      : fresh.some((service) => displayedTone(service) === "yellow")
        ? "yellow"
        : unknown > 0 || fresh.some((service) => displayedTone(service) === "gray") ? "gray" : "green";
  const sub = fresh.length === 0
    ? "Keine frische Health-Prüfung"
    : `${healthy}/${services.length} aktuell gesund${unknown > 0 ? ` · ${unknown} unbekannt/veraltet` : ""}`;

  return (
    <div className="mon-ampel-grid">
      <div className="mon-ampel-item" data-testid="monitoring-summary">
        <AmpelDot tone={tone} size={12} />
        <div className="mon-ampel-text">
          <span className="mon-ampel-label">Registrierte Anwendungen</span>
          <span className="mon-ampel-sub">{sub}</span>
        </div>
      </div>
    </div>
  );
}

function ServiceCard({ service }: { service: HealthService }) {
  const freshness = serviceFreshness(service);
  const tone = displayedTone(service);
  const isFresh = freshness === "fresh";
  const canShowAvailability = service.uptimePct != null && service.coverage !== "no_samples";

  return (
    <div
      className="mon-service-card"
      data-testid={`monitoring-service-${service.appClaim}`}
      style={{ borderLeftColor: toneToColor(tone) }}
    >
      <div className="mon-service-header">
        <span className="mon-service-name">{service.name}</span>
        <AmpelDot tone={tone} />
      </div>

      <div className="mon-service-meta">
        <div className="mon-service-row">
          <span className="mon-service-label">Status</span>
          <span className={`mon-service-value mon-service-value--${tone}`}>{statusLabel(service)}</span>
        </div>
        <div className="mon-service-row">
          <span className="mon-service-label">Letzte Prüfung</span>
          <span className="mon-service-value">{formatTimeAgo(service.lastCheckedAt)}</span>
        </div>
        <div className="mon-service-row">
          <span className="mon-service-label">Datenalter</span>
          <span className="mon-service-value">{formatAge(service.dataAgeMs)}</span>
        </div>
        <div className="mon-service-row">
          <span className="mon-service-label">24-h-Nachweis</span>
          <span className="mon-service-value">{coverageLabel(service)}</span>
        </div>
        {isFresh && service.lastLatencyMs != null && (
          <div className="mon-service-row">
            <span className="mon-service-label">Latenz</span>
            <span className="mon-service-value">{service.lastLatencyMs} ms</span>
          </div>
        )}
        {canShowAvailability && (
          <div className="mon-service-row">
            <span className="mon-service-label">
              {service.coverage === "full_24h" ? "Erreichbarkeit (24 h)" : "Erreichbarkeit (Stichprobe)"}
            </span>
            <span className="mon-service-value">{service.uptimePct}%</span>
          </div>
        )}
        {service.lastError && isFresh && (
          <div className="mon-service-row">
            <span className="mon-service-label">Letzter Fehler</span>
            <span className="mon-service-value">{service.lastError}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function OperationsSources({ sources }: { sources: MonitoringData["sources"] }) {
  const coolify = sources?.coolify ?? {
    connected: false,
    status: "unknown" as const,
    note: "Coolify ist Betriebsquelle für Deployment und Laufzeit; diese Ansicht fragt keine Coolify-API ab.",
  };
  const uptimeRobot = sources?.uptimeRobot ?? {
    connected: false,
    status: "unknown" as const,
    note: "Externe UptimeRobot-Daten sind unbekannt: nicht angebunden.",
  };

  return (
    <div className="mon-card" data-testid="monitoring-sources">
      <div className="mon-card-head">
        <span className="mon-card-title">Betriebsquellen</span>
      </div>
      <div className="mon-card-body">
        <div className="mon-service-row">
          <span className="mon-service-label">Coolify</span>
          <span className="mon-service-value">{coolify.note}</span>
        </div>
        <div className="mon-service-row">
          <span className="mon-service-label">UptimeRobot</span>
          <span className="mon-service-value">{uptimeRobot.note}</span>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────

export function MonitoringPage() {
  const [data, setData] = useState<MonitoringData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [pollingPaused, setPollingPaused] = useState(false);
  const [, refreshClock] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);
  const requestSequenceRef = useRef(0);
  const mountedRef = useRef(false);
  const hasLoadedRef = useRef(false);

  const fetchData = useCallback((showLoading = false): Promise<void> => {
    // Dedupliziert Polling, Resume und manuellen Refresh; dadurch kann keine
    // ältere parallele Antwort einen neueren Snapshot überschreiben.
    if (inFlightRef.current) return inFlightRef.current;

    const sequence = ++requestSequenceRef.current;
    if (showLoading && mountedRef.current) setLoading(true);
    if (mountedRef.current) setError(null);

    const request = (async () => {
      try {
        const res = await fetch("/api/monitoring/status", {
          credentials: "include",
          headers: {
            "Cache-Control": "no-cache",
            [MONITORING_CONTRACT_HEADER]: MONITORING_CONTRACT_VERSION,
          },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const clearInvalidData = () => {
          if (mountedRef.current && sequence === requestSequenceRef.current) {
            setData(null);
            setLastUpdated(null);
          }
        };
        let json: unknown;
        try {
          json = await res.json();
        } catch {
          clearInvalidData();
          throw new Error(INVALID_MONITORING_RESPONSE);
        }
        if (!isMonitoringData(json)) {
          clearInvalidData();
          throw new Error(INVALID_MONITORING_RESPONSE);
        }
        if (mountedRef.current && sequence === requestSequenceRef.current) {
          setData(json);
          setLastUpdated(new Date());
          hasLoadedRef.current = true;
        }
      } catch (err) {
        if (mountedRef.current && sequence === requestSequenceRef.current) {
          const msg = err instanceof Error ? err.message : String(err);
          setError(msg);
        }
      } finally {
        if (mountedRef.current && sequence === requestSequenceRef.current) setLoading(false);
      }
    })();

    inFlightRef.current = request;
    void request.then(
      () => { if (inFlightRef.current === request) inFlightRef.current = null; },
      () => { if (inFlightRef.current === request) inFlightRef.current = null; },
    );
    return request;
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const clearPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const syncPolling = () => {
      const allowed = pollingAllowed();
      setPollingPaused(!allowed);
      if (!allowed) {
        clearPolling();
        return;
      }
      void fetchData(!hasLoadedRef.current);
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          if (pollingAllowed()) void fetchData(false);
        }, 60_000);
      }
    };

    syncPolling();
    document.addEventListener("visibilitychange", syncPolling);
    window.addEventListener("online", syncPolling);
    window.addEventListener("offline", syncPolling);
    return () => {
      mountedRef.current = false;
      clearPolling();
      document.removeEventListener("visibilitychange", syncPolling);
      window.removeEventListener("online", syncPolling);
      window.removeEventListener("offline", syncPolling);
    };
    // A visibility/online resume must reuse the same safe fetch function; the
    // separate ref, rather than render state, preserves the first-spinner rule.
  }, [fetchData]);

  useEffect(() => {
    // Reines UI-Alterungssignal, kein Request und keine Sitzungsaktivität.
    const clock = window.setInterval(() => refreshClock((value) => value + 1), 30_000);
    return () => window.clearInterval(clock);
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!pollingAllowed()) {
      setError("Offline oder nicht sichtbar: Monitoring-Aktualisierung ist ausgesetzt.");
      return;
    }
    await fetchData(true);
  }, [fetchData]);

  const services = data?.health.services ?? [];

  return (
    <div className="mon-root">
      <div className="mon-header">
        <div className="mon-header-left">
          <Activity size={18} strokeWidth={1.8} style={{ color: "var(--accent)" }} />
          <h1 className="mon-title">Monitoring</h1>
        </div>
        <div className="mon-header-right">
          {lastUpdated && (
            <span className="mon-meta">
              Aktualisiert: {lastUpdated.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <span className="mon-meta">
            {pollingPaused ? "Aktualisierung pausiert (Tab verborgen/offline)" : "Auto-Refresh: 60 s"}
          </span>
          <button
            type="button"
            className="mon-btn"
            onClick={handleRefresh}
            disabled={loading || pollingPaused}
          >
            <RefreshCw size={13} strokeWidth={2} className={loading ? "spin" : ""} />
            {loading ? "Laden…" : "Aktualisieren"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mon-error" role="alert">
          <AlertTriangle size={14} strokeWidth={2} />
          <span>{error}</span>
        </div>
      )}

      {loading && !data && (
        <div className="mon-loading">
          <Loader size={16} strokeWidth={2} className="spin" />
          <span>Lade aktuelle Health-Daten…</span>
        </div>
      )}

      {data && (
        <>
          <div className="mon-section">
            <h2 className="mon-section-title">Aktueller Health-Status</h2>
            <AmpelSummary services={services} />
          </div>

          <div className="mon-section">
            <OperationsSources sources={data.sources} />
          </div>

          <div className="mon-section">
            <h2 className="mon-section-title">
              Registrierte Anwendungen
              <span className="mon-pill">{services.length}</span>
            </h2>
            {services.length === 0 ? (
              <div className="mon-card">
                <div className="mon-card-body">
                  <span style={{ color: "var(--text-3)", fontSize: 12 }}>
                    In dieser Instanz sind keine Satellite-Apps für Healthchecks registriert.
                  </span>
                </div>
              </div>
            ) : (
              <div className="mon-services-grid">
                {services.map((service) => <ServiceCard key={service.appClaim} service={service} />)}
              </div>
            )}
          </div>

          {data.health.alerts.length > 0 && (
            <div className="mon-section">
              <div className="mon-card mon-card--alert">
                <div className="mon-card-head">
                  <AlertTriangle size={14} strokeWidth={2} style={{ color: "var(--bad)" }} />
                  <span className="mon-card-title">Aktive Health-Alerts</span>
                  <span className="mon-pill mon-pill--bad">{data.health.alerts.length}</span>
                </div>
                <div className="mon-card-body">
                  {data.health.alerts.map((alert) => (
                    <div key={alert.appClaim} className="mon-alert-row">
                      <AmpelDot tone="red" />
                      <span className="mon-alert-name">{alert.name}</span>
                      <span className="mon-alert-detail">{alert.consecutiveFailures}× fehlgeschlagen</span>
                      <span className="mon-alert-since">seit {alert.since ? formatTimeAgo(alert.since) : "—"}</span>
                      {alert.lastError && <span className="mon-alert-error">{alert.lastError}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="mon-section">
            <div className="mon-meta" style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Clock size={12} />
              Healthchecks laufen intern im Abstand von {data.health.intervalSeconds} Sekunden. Veraltete oder fehlende Daten bleiben neutral.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
